// @flow
import { createSelector } from "reselect";
import {
  groupBy,
  indexBy,
  intersection,
  map,
  path,
  prop,
  props,
  propOr,
  isEmpty,
} from "ramda";
import { Maybe } from "ramda-fantasy";
import type {
  Id,
  Medida,
  MedidaTraduzida,
  ModoExibicaoMedida,
  AjusteDias,
  Periodicidade,
  Periodo,
  ListaPeriodosPorTipo,
  ChaveEnum,
  VersaoConta,
  EnumCodigoDescricao,
  Indice,
  TaxaDeAdministracao,
  Volatilidade,
  TipoDoRetorno,
  MesEAnoComposto,
  Deflator,
  CorretorMonetario,
  EnumIdChaveTraducao,
  GrupamentoMedida,
  FiltroTipoAtivo,
  BenchmarkPersonalizado,
  FuncaoRankingTraduzida,
  Traduzido,
  VisualizacaoCarteira,
  RetornoNumerador,
  CalculoNumerador,
  TipoVolatilidade,
  EstimacaoVolatilidade,
  TipoRetornoEstatistica,
  PeriodicidadeEstatistica,
  TipoIdentificadorAtivo,
  TipoAjustePreco,
  EscalaMoeda,
  FonteFidc,
  FormaDeVisualizacaoSeries,
} from "../../model";
import {
  OPCOES_DATA_BASE_DEFLATOR_MODO_NORMAL,
  OPCOES_DATA_BASE_DEFLATOR_MODO_SERIES,
  TIPO_ATIVO_INDICES,
  MODO_APP_SERIES,
  GRUPAMENTO_MEDIDAS_SERIES,
  CHAVE_TIPO_INDICE_FTSE,
} from "../../model";
import { findEmLista } from "../../util";
import {
  type State,
  type SimpleSelector,
  type MapConjuntoPermissaoMedidasPorTipoAtivo,
} from "..";
import { getMapConjuntoPermissaoMedidasPorTipoAtivo } from "./usuarioSelectors";
import { getDataBaseSelecionada } from "./dataBaseSelectors";
import { getMedidasSuportadasExcel } from "./integracaoExcel";
import {
  getFiltrosTipoAtivo,
  getFiltrosTipoAtivoSelecionados,
  getGrupamentoMedidaSelecionado,
} from "./filtroMedidasSelectors";
import { i18n } from "./i18n";
import Moment from "moment";
import { type I18n } from "@quantum/i18n";
import { getModoApp } from "./comum";

export const getPeriodicidades = (state: State): Array<Periodicidade> =>
  state.dominio.periodicidades;

export const getAjusteDias = (state: State): Array<AjusteDias> =>
  state.dominio.ajustesDias;

export const getModosExibicao = (state: State): Array<ModoExibicaoMedida> =>
  state.dominio.modosExibicao;

export const getMedidas = (state: State): Array<Medida> =>
  state.dominio.medidas;

export const getTiposAjustePreco = (state: State): Array<TipoAjustePreco> =>
  state.dominio.tiposAjuste;

export const getTiposAjustePrecoSemProventos = (
  state: State
): Array<TipoAjustePreco> => state.dominio.tiposAjusteSemProventos;

export const getEscalaMoedas = (state: State): Array<EscalaMoeda> => {
  return state.dominio.escalas;
};

export const getFontesFidc = (state: State): Array<FonteFidc> =>
  state.dominio.fontesDeDadosFIDC;

const getCamposMedidasSecundariasOrdenada = createSelector(
  state => state.dominio.camposMedidasSecundarias,
  medidas => medidas.map((medida, ordem) => ({ ...medida, ordem }))
);

const camposMedidasSecundariasPorMedida = createSelector(
  getCamposMedidasSecundariasOrdenada,
  indexBy(prop("idMedida"))
);

export const getCamposPorMedida = createSelector(
  camposMedidasSecundariasPorMedida,
  i18n,
  (campos, { translate }) =>
    map(
      ({ chaveTraducao, ...campo }) => ({
        ...campo,
        nome: translate(chaveTraducao),
      }),
      campos
    )
);

export const getOrdemPorMedida: (Medida, State) => number = (medida, state) =>
  propOr(
    Infinity,
    medida.codigoOriginal,
    map(prop("ordem"), getCamposPorMedida(state))
  );

const getMedidasIndexadasPorGrupamento: SimpleSelector<{
  [GrupamentoMedida]: Array<Medida>,
}> = createSelector(getMedidas, groupBy(path(["grupamentoMedida", "nome"])));

export const getMedidasPorGrupamento = (
  state: State,
  grupamento: string
): Array<Medida> => prop(grupamento, getMedidasIndexadasPorGrupamento(state));

export const getMedidasTraduzidas: State => Array<MedidaTraduzida> = createSelector(
  getMedidas,
  i18n,
  (medidas: Array<Medida>, { lookup }: I18n<any>) =>
    medidas.map(medida => {
      const chaveTraducao = medida.nome.replace(/\s/g, "");
      const medidaTraduzida = lookup(chaveTraducao);

      return {
        ...medida,
        // A chave de tradução das medidas é o nome menos os espaços,
        // porém varias medidas nao possuem traducao pra pt-br,
        // e nesses casos a traducao deve ser o proprio nome.
        traducao:
          medidaTraduzida === chaveTraducao
            ? medida.nome
            : medidaTraduzida || medida.nome,
      };
    })
);

export const getMedidasPermitidas: State => Array<MedidaTraduzida> = createSelector(
  getMedidasTraduzidas,
  getMapConjuntoPermissaoMedidasPorTipoAtivo,
  getFiltrosTipoAtivo,
  getMedidasSuportadasExcel,
  (
    medidas: Array<MedidaTraduzida>,
    permissoes: MapConjuntoPermissaoMedidasPorTipoAtivo,
    filtrosTipoAtivo: Array<FiltroTipoAtivo>,
    medidasSuportadasExcel: ?Set<Id>
  ) =>
    medidas
      .filter(medida =>
        filtrosTipoAtivo
          .filter(filtro => medida.filtros.includes(filtro.id))
          .some(
            ({ nome }) =>
              propOr([], nome, permissoes).includes(medida.conjuntoPermissao) ||
              // Todo os usuários tem acesso as medidas de índice, independente do nível.
              nome === TIPO_ATIVO_INDICES
          )
      )
      .filter(
        medida =>
          medidasSuportadasExcel == null ||
          medidasSuportadasExcel.has(medida.idConfiguracaoRelatorio)
      )
);

export const getMedidasAgrupadas: (
  state: State
) => { [string]: Array<MedidaTraduzida> } = createSelector(
  getMedidasPermitidas,
  groupBy(path(["grupamentoMedida", "nome"]))
);

export const getMedidasFiltradas: State => Array<MedidaTraduzida> = createSelector(
  getMedidasPermitidas,
  getGrupamentoMedidaSelecionado,
  getFiltrosTipoAtivoSelecionados,
  (
    medidas: Array<MedidaTraduzida>,
    grupamentoSelecionado: string,
    filtrosTipoAtivoSelecionados: Array<Id>
  ) =>
    medidas.filter(
      medida =>
        medida.grupamentoMedida.nome === grupamentoSelecionado &&
        !isEmpty(intersection(medida.filtros, filtrosTipoAtivoSelecionados))
    )
);

export const getPeriodos = (state: State): Array<Periodo> =>
  state.dominio.periodos;

export const getPeriodosPorTipoLista = (state: State): ListaPeriodosPorTipo =>
  state.dominio.periodosPorTipoLista;

export const getTiposConta = (state: State): Array<EnumCodigoDescricao> =>
  state.dominio.tiposConta;

export const getVersoesConta = (state: State): Array<VersaoConta> =>
  state.dominio.versoesConta;

export const getFormasAcumulacao = (state: State): Array<EnumCodigoDescricao> =>
  state.dominio.formasAcumulacao;

export const getCapitaisUtilizados = (
  state: State
): Array<EnumCodigoDescricao> => state.dominio.capitaisUtilizados;

export const getAnos = (state: State): Array<EnumCodigoDescricao> =>
  state.dominio.anos;

export const getTiposReferencia = (state: State): Array<EnumCodigoDescricao> =>
  state.dominio.tiposReferencia;

export const getTiposDataReferencia = (
  state: State
): Array<EnumCodigoDescricao> => state.dominio.tiposDataReferencia;

export const getTrimestresReferencia = (
  state: State
): Array<EnumCodigoDescricao> => state.dominio.trimestresReferencia;

export const getFrequeciasIndicador = (
  state: State
): Array<EnumCodigoDescricao> => state.dominio.frequeciasIndicador;

export const getSeparadorDecimal = (state: State): string =>
  state.dominio.separadorDecimal;

export const getIndices = (state: State): Array<Indice> =>
  state.dominio.indices;

export const getBenchmarksPersonalizados: SimpleSelector<
  Array<BenchmarkPersonalizado>
> = createSelector(
  state => state.dominio.benchmarksPersonalizados,
  map(benchmark => ({
    nome: benchmark,
    nomeTraduzido: benchmark,
  }))
);

export const getIndicesEBenchmarksPersonalizados: State => Array<
  Indice | BenchmarkPersonalizado
> = createSelector(
  getIndices,
  getBenchmarksPersonalizados,
  (indices = [], benchmarksPersonalizados = []) => [
    ...indices,
    ...benchmarksPersonalizados,
  ]
);

export const getTaxasDeAdministracao = (
  state: State,
  incluirTaxaEfetiva: boolean = false
): Array<TaxaDeAdministracao> =>
  incluirTaxaEfetiva
    ? state.dominio.taxasDeAdministracao
    : state.dominio.taxasDeAdministracao.filter(taxa => !taxa.efetivo);

export const getVolatilidades = (state: State): Array<Volatilidade> =>
  state.dominio.volatilidades;

export const getTiposDoRetorno = (state: State): Array<TipoDoRetorno> =>
  state.dominio.tiposDoRetorno;

export const getMesesEAnosCompostos = (state: State): Array<MesEAnoComposto> =>
  state.dominio.mesesEAnosCompostos;

export const getFrequenciasDisponiveis = (
  state: State
): Array<EnumCodigoDescricao> => state.dominio.frequeciasIndicador;

export const getPlsUtilizadoDisponiveis = (
  state: State
): Array<EnumCodigoDescricao> => state.dominio.plsUtilizado;

export const getTodosCorretoresMonetarios = (
  state: State
): Array<CorretorMonetario> => state.dominio.corretoresMonetarios;

export const getCorretoresMonetariosSemFTSE: SimpleSelector<
  Array<CorretorMonetario>
> = createSelector(getTodosCorretoresMonetarios, corretores =>
  corretores.filter(corretor => corretor.tipoIndice !== CHAVE_TIPO_INDICE_FTSE)
);

export const getCorretoresMonetarios: SimpleSelector<
  Array<CorretorMonetario>
> = createSelector(
  getModoApp,
  getGrupamentoMedidaSelecionado,
  getTodosCorretoresMonetarios,
  getCorretoresMonetariosSemFTSE,
  (modoApp, grupamentoSelecionado, todosCorretores, corretoresSemFTSE) =>
    modoApp === MODO_APP_SERIES &&
    grupamentoSelecionado === GRUPAMENTO_MEDIDAS_SERIES
      ? todosCorretores
      : corretoresSemFTSE
);

export const getTodosDeflatores = (state: State): Array<Deflator> =>
  state.dominio.deflatores;

export const getDeflatoresSemFTSE: SimpleSelector<
  Array<Deflator>
> = createSelector(getTodosDeflatores, deflatores =>
  deflatores.filter(deflator => deflator.tipoIndice !== CHAVE_TIPO_INDICE_FTSE)
);

export const getDeflatores: SimpleSelector<
  Array<Deflator>
> = createSelector(
  getModoApp,
  getGrupamentoMedidaSelecionado,
  getTodosDeflatores,
  getDeflatoresSemFTSE,
  (modoApp, grupamentoSelecionado, todosDeflatores, deflatoresSemFTSE) =>
    modoApp === MODO_APP_SERIES &&
    grupamentoSelecionado === GRUPAMENTO_MEDIDAS_SERIES
      ? todosDeflatores
      : deflatoresSemFTSE
);

export const getValoresBaseDeflator = (
  state: State
): Array<EnumCodigoDescricao> => state.dominio.valoresBaseDeflator;

export const getDatasBaseDeflator = (
  state: State
): Array<EnumCodigoDescricao> => state.dominio.datasBaseDeflator;

export const getDatasBaseDeflatorPorValorBaseSelecionado = (
  state: State
): Array<EnumCodigoDescricao> => {
  const opcoesDisponiveis =
    getModoApp(state) === MODO_APP_SERIES
      ? OPCOES_DATA_BASE_DEFLATOR_MODO_SERIES
      : OPCOES_DATA_BASE_DEFLATOR_MODO_NORMAL;
  return state.dominio.datasBaseDeflator.filter(dataBase => {
    return opcoesDisponiveis.indexOf(dataBase.codigo) >= 0;
  });
};

export const getDataBaseDefault = (state: State): string =>
  state.dominio.dataBaseDefault;

export const getDataBaseMaxima = (state: State): string =>
  state.dominio.dataBaseMaxima;

export const getTiposJanela = (state: State): Array<EnumIdChaveTraducao> =>
  state.dominio.tiposJanela;

const gerarUltimos4Meses = (dataBase, mesesEAnosCompostos) => {
  const mesAnterior = Moment(dataBase)
    .subtract(1, "month")
    .format("MMM/YYYY")
    .toLowerCase();
  const indiceMesAnterior = mesesEAnosCompostos.findIndex(
    ({ nome }) => nome.toLowerCase() === mesAnterior
  );

  return mesesEAnosCompostos.slice(indiceMesAnterior, indiceMesAnterior + 4);
};

export const getUltimos4MesesCompostos: SimpleSelector<
  Array<MesEAnoComposto>
> = createSelector(
  getDataBaseSelecionada,
  getMesesEAnosCompostos,
  gerarUltimos4Meses
);

export const getUltimos4MesesCompostosComDataBaseDefault: SimpleSelector<
  Array<MesEAnoComposto>
> = createSelector(
  getDataBaseDefault,
  getMesesEAnosCompostos,
  gerarUltimos4Meses
);

export const getGrupamentosMedida = (state: State): Array<GrupamentoMedida> =>
  state.dominio.grupamentosMedida;

const getNomesGrupamentosUtilizados: SimpleSelector<
  Array<string>
> = createSelector(
  getMedidasPermitidas,
  medidas => new Set(medidas.map(medida => medida.grupamentoMedida.nome))
);

export const getGrupamentosUtilizados: SimpleSelector<
  Array<GrupamentoMedida>
> = createSelector(
  getGrupamentosMedida,
  getNomesGrupamentosUtilizados,
  (grupamentos, grupamentosUtilizados) =>
    grupamentos.filter(grupamento => grupamentosUtilizados.has(grupamento.nome))
);

export const getGrupamentosMedidaTraduzidos: (
  state: State
) => Array<Traduzido<GrupamentoMedida>> = createSelector(
  getGrupamentosUtilizados,
  i18n,
  (grupamentos, { translate }) =>
    grupamentos.map(grupamento => ({
      ...grupamento,
      traducao: translate(grupamento.chaveTraducao).toUpperCase(),
    }))
);

export const getGrupamentosMedidaRelacionamentos: (
  state: State
) => Array<Traduzido<GrupamentoMedida>> = createSelector(
  getGrupamentosMedidaTraduzidos,
  grupamentos => grupamentos
);

export const getDiasUteis = (state: State): Array<string> =>
  state.dominio.diasUteis;

export const getTrimestreBaseEmpresasAbertas = (state: State): ?number =>
  state &&
  state.dominio &&
  state.dominio.trimestreEAnoBaseEmpresasAbertas &&
  parseInt(
    Object.entries(state.dominio.trimestreEAnoBaseEmpresasAbertas)[0][0],
    10
  );

export const getAnoBaseEmpresasAbertas = (state: State): ?number =>
  state &&
  state.dominio &&
  state.dominio.trimestreEAnoBaseEmpresasAbertas &&
  parseInt(
    Object.entries(state.dominio.trimestreEAnoBaseEmpresasAbertas)[0][1],
    10
  );

const getMedidasPorGrupamentoMedidaSelecionado = createSelector(
  getGrupamentoMedidaSelecionado,
  getMedidasIndexadasPorGrupamento,
  (grupamento, medidasPorGrupamento) => medidasPorGrupamento[grupamento]
);

export const getMedidasIndexadasPorId: SimpleSelector<{
  [Id]: Medida,
}> = createSelector(
  getMedidasPorGrupamentoMedidaSelecionado,
  indexBy(prop("idConfiguracaoRelatorio"))
);

export const getMedidasPorId = (state: State) => (
  ids: Array<Id>
): Array<Medida> => props(ids, getMedidasIndexadasPorId(state));

export const getMedidaPorId = (state: State) => (id: ?Id): ?Medida =>
  prop(id, getMedidasIndexadasPorId(state));

export const getModoExibicaoPorIdMedida = (state: State) => (
  id: ?Id
): ?ModoExibicaoMedida =>
  Maybe(getMedidaPorId(state)(id))
    .map(medida =>
      getModoApp(state) === MODO_APP_SERIES
        ? Maybe(medida.modoExibicaoModoSeries).getOrElse(medida.modoExibicao)
        : medida.modoExibicao
    )
    .getOrElse();

export const getPeriodoPorId = (state: State) => (id: Id): Periodo =>
  findEmLista(id, getPeriodos(state));

export const getAjusteDiasPorId = (state: State) => (id: ?Id): ?AjusteDias => {
  if (id !== null && id !== undefined) {
    return findEmLista(id, getAjusteDias(state));
  } else {
    return null;
  }
};

export const getModoExibicaoPorChave = (state: State) => (
  chave: ChaveEnum
): ModoExibicaoMedida => findEmLista(chave, getModosExibicao(state), "chave");

export const getDataFechamentoMesAnterior = (state: State): ?number => {
  return state.dominio.dataFechamentoMesAnterior;
};

export const getVisualizacoesCarteira = (
  state: State
): ?Array<VisualizacaoCarteira> => {
  return state.dominio.formasVisualizacaoCarteira;
};

export const getRetornoNumerador = (state: State): ?Array<RetornoNumerador> => {
  return state.dominio.tiposRetornoNumerador;
};

export const getCalculoNumerador = (state: State): ?Array<CalculoNumerador> => {
  return state.dominio.tiposCalculoNumerador;
};

export const getPeriodicidadeEstatistica = (
  state: State
): ?Array<PeriodicidadeEstatistica> => {
  return state.dominio.periodicidadeEstatistica;
};

export const getTipoVolatilidade = (state: State): ?Array<TipoVolatilidade> => {
  return state.dominio.tiposVolatilidade;
};

export const getEstimacaoVolatilidade = (
  state: State
): ?Array<EstimacaoVolatilidade> => {
  return state.dominio.estimacaoVolatilidade;
};

export const getTipoRetornoEstatistica = (
  state: State
): ?Array<TipoRetornoEstatistica> => {
  return state.dominio.tiposRetornoEstatistica;
};

export const getFuncoesRanking: (
  state: State
) => Array<FuncaoRankingTraduzida> = createSelector(
  i18n,
  state => state.dominio.funcoesRanking,
  ({ translate }, funcoesRanking) =>
    funcoesRanking.map(funcao => ({
      ...funcao,
      traducao: translate(funcao.chaveTraducao),
    }))
);

const funcoesRankingPorId = createSelector(
  getFuncoesRanking,
  indexBy(prop("id"))
);
const funcoesRankingPorFuncao = createSelector(
  getFuncoesRanking,
  indexBy(prop("funcao"))
);

export const getDescricaoFuncaoRankingPorId = (id: number, state: State) =>
  Maybe(funcoesRankingPorId(state)[id])
    .map(prop("traducao"))
    .getOrElse("Ranking");

export const getNomeFuncaoRankingPorId = (id: number, state: State) =>
  Maybe(funcoesRankingPorId(state)[id])
    .map(prop("funcao"))
    .getOrElse("ranking");

export const getIdFuncaoRankingPorFuncao = (
  funcao: string,
  state: State
): ?number =>
  Maybe(funcoesRankingPorFuncao(state)[funcao])
    .map(prop("id"))
    .getOrElse(null);

export const getFormatosMedidasCompostas = createSelector(
  i18n,
  state => state.dominio.formatacaoMedidasCompostas,
  ({ translate }, formatos) =>
    formatos.map(formato => ({
      ...formato,
      traducao: translate(formato.chaveTraducao),
    }))
);

const getPeriodicidadeSelecionada = (state: State): ?Id =>
  state.edicaoMedida.GLOBAL.periodicidade;

export const getPeriodosSeries: State => Array<{
  ...Periodo,
  descricao: string,
}> = createSelector(
  i18n,
  createSelector(getPeriodos, indexBy(prop("id"))),
  getPeriodosPorTipoLista,
  getPeriodicidadeSelecionada,
  ({ translate }, periodos, tipos, periodicidadeSelecionada) =>
    (tipos.SERIES || [])
      .map(id => periodos[id])
      .filter(periodo =>
        periodo.periodicidades.includes(periodicidadeSelecionada)
      )
      .map(periodo => ({
        ...periodo,
        descricao: translate(periodo.chaveTraducao) || periodo.chaveTraducao,
      }))
);

export const getListaDeCasasDecimais = (state: State): * => [
  // TODO: Remover comentário do padrão antigo no futuro se o padrão atual se consolidar
  // Padrão antigo: { id: -1, descricao: "configuracaoRelatorio.padrao" }
  { id: 0, descricao: "0" },
  { id: 1, descricao: "1" },
  { id: 2, descricao: "2" },
  { id: 3, descricao: "3" },
  { id: 4, descricao: "4" },
  { id: 5, descricao: "5" },
  { id: 6, descricao: "6" },
  { id: 7, descricao: "7" },
  { id: 8, descricao: "8" },
  { id: 9, descricao: "9" },
  { id: 10, descricao: "10" },
  { id: 11, descricao: "11" },
  { id: 12, descricao: "12" },
  { id: 13, descricao: "13" },
  { id: 14, descricao: "14" },
];

export const getListaModosVisualizacaoSeries = (
  state: State
): Array<FormaDeVisualizacaoSeries> =>
  state.dominio.formasDeVisualizacaoRelatorioSeries;

export const getTiposIdentificadorAtivoSeries: State => Array<TipoIdentificadorAtivo> = createSelector(
  i18n,
  state => state.dominio.tiposIdentificadorAtivo,
  ({ translate }, tipos) =>
    tipos.map(tipo => ({ ...tipo, descricao: translate(tipo.chaveTraducao) }))
);
